import axiosInstance from "../interceptors";
import {
  INTERVIEW_API_BASE_URL,
  INTERVIEW_API_KEY,
} from "../config/config";

// The interview API is a dedicated service (was https://interviewurl.vartulz.com).
// When REACT_APP_INTERVIEW_API_BASE_URL is unset, INTERVIEW_API_BASE_URL falls back
// to the gateway baseUrl in config.js — but the gateway has no /api/v1/interviews
// route, so every call returned 404. Treat "no dedicated URL configured" as
// "service unavailable" and short-circuit instead of hitting the gateway.
const HAS_DEDICATED_HOST =
  !!process.env.REACT_APP_INTERVIEW_API_BASE_URL;

// Tenant scoping: every interview call is qualified with the caller's tenant,
// read from localStorage (same key/fallback the rest of the app uses). Appended
// as a tenant_id query param so it survives any sub-path (report-json,
// transcript, recording-url, …).
const withTenant = (url) => {
  const tenantId = localStorage.getItem("tenantId") || "1";
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}tenant_id=${encodeURIComponent(tenantId)}`;
};

export const interviewApi = (id, path = "") =>
  withTenant(
    path
      ? `${INTERVIEW_API_BASE_URL}/api/v1/interviews/${id}/${path}`
      : `${INTERVIEW_API_BASE_URL}/api/v1/interviews/${id}`,
  );

// Single GET wrapper for the interview API. Adds the X-API-Key header and
// bypasses the ngrok-free browser-warning splash; preserves any per-call
// options (e.g. AbortController signal).
export const getInterview = (id, path = "", opts = {}) => {
  if (!HAS_DEDICATED_HOST) {
    return Promise.reject(
      new Error(
        "interview-api: REACT_APP_INTERVIEW_API_BASE_URL not set; skipping call"
      )
    );
  }
  return axiosInstance.get(interviewApi(id, path), {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      "X-API-Key": INTERVIEW_API_KEY,
      "ngrok-skip-browser-warning": "true",
    },
  });
};

// POST /api/v1/interviews/scores — batch score/recommendation lookup. Replaces
// N per-interview report-json calls with one request. Tenant scoping is supplied
// by the caller (read from localStorage at the call site so this helper stays
// UI-agnostic). Response shape: { tenant_id, results: [{ interview_id, found,
// status, overall_score, recommendation }] }.
export const getInterviewScores = (tenantId, interviewIds, opts = {}) => {
  if (!HAS_DEDICATED_HOST) {
    return Promise.reject(
      new Error(
        "interview-api: REACT_APP_INTERVIEW_API_BASE_URL not set; skipping call"
      )
    );
  }
  return axiosInstance.post(
    `${INTERVIEW_API_BASE_URL}/api/v1/interviews/scores`,
    { tenant_id: tenantId, interview_ids: (interviewIds || []).map(String) },
    {
      ...opts,
      headers: {
        ...(opts.headers || {}),
        "X-API-Key": INTERVIEW_API_KEY,
        "ngrok-skip-browser-warning": "true",
      },
    },
  );
};

// POST /api/v1/interviews/compare — side-by-side comparison across N interviews.
// Tenant scoping is supplied by the caller (read from localStorage at the call
// site so this helper stays UI-agnostic).
export const compareInterviews = (tenantId, interviewIds, opts = {}) => {
  if (!HAS_DEDICATED_HOST) {
    return Promise.reject(
      new Error(
        "interview-api: REACT_APP_INTERVIEW_API_BASE_URL not set; skipping call"
      )
    );
  }
  return axiosInstance.post(
    `${INTERVIEW_API_BASE_URL}/api/v1/interviews/compare`,
    { tenant_id: tenantId, interview_ids: (interviewIds || []).map(String) },
    {
      ...opts,
      headers: {
        ...(opts.headers || {}),
        "X-API-Key": INTERVIEW_API_KEY,
        "ngrok-skip-browser-warning": "true",
      },
    },
  );
};
